import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  Globe, Search, Loader, Download, RefreshCw, Plane, Ship, Newspaper,
  Satellite, AlertTriangle, ChevronDown, ChevronUp, CheckCircle, MapPin,
  Clock, Shield, TrendingUp, Activity, FileText, Zap, Target, Flag, Layers
} from 'lucide-react';
import {
  ClassificationBanner,
  ReportHeader,
  ExecutiveSummary,
  ReportSection,
  ThreatAssessmentPanel,
  RecommendationsPanel,
  ReportFooter,
  IntelligenceReport
} from './IntelligenceReportUI';

export default function CountryAnalysis() {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    executive: true,
    activities: true,
    flights: false,
    vessels: false,
    news: false,
    satellite: false,
    recommendations: true
  });
  const [timeRange, setTimeRange] = useState(24);
  const [includeOptions, setIncludeOptions] = useState({
    flights: true,
    vessels: true,
    news: true,
    satellite: true
  });

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const response = await axios.get(`${API}/analysis/countries`);
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAnalysis = async () => {
    if (!selectedCountry) return;
    
    setAnalyzing(true);
    setReport(null);
    
    try {
      const response = await axios.post(`${API}/analysis/country`, {
        country_id: selectedCountry.id,
        include_flights: includeOptions.flights,
        include_vessels: includeOptions.vessels,
        include_news: includeOptions.news,
        include_satellite: includeOptions.satellite,
        time_range_hours: timeRange,
        classification_level: "UNCLASSIFIED"
      });
      setReport(response.data);
      setExpandedSections({
        executive: true,
        activities: true,
        flights: true,
        vessels: true,
        news: true,
        satellite: false,
        recommendations: true
      });
    } catch (error) {
      console.error('Error generating analysis:', error);
      alert('Failed to generate analysis. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const exportToPptx = async () => {
    if (!report) return;
    
    try {
      const response = await axios.post(
        `${API}/analysis/export-pptx/${report.id}`,
        {},
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Intel_Report_${report.country_code}_${report.id.slice(0,8)}.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export report');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getRegionColor = (region) => {
    const colors = {
      'Middle East': 'text-orange-400',
      'East Asia': 'text-cyan-400',
      'Europe': 'text-blue-400',
      'Eastern Europe': 'text-purple-400',
      'South Asia': 'text-yellow-400',
      'Southeast Asia': 'text-emerald-400',
      'North America': 'text-red-400',
      'South America': 'text-green-400',
      'Africa': 'text-amber-400',
      'North Africa': 'text-orange-300',
      'Oceania': 'text-teal-400',
      'Eurasia': 'text-indigo-400'
    };
    return colors[region] || 'text-zinc-400';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader className="w-8 h-8 text-[#00FF9D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="country-analysis">
      {/* Header */}
      <div className="glass-panel border-b border-white/10 p-6">
        <div className="flex items-center gap-3">
          <Globe className="w-8 h-8 text-[#00FF9D]" />
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
              COUNTRY INTELLIGENCE ANALYSIS
            </h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              Multi-source intelligence fusion • Flight • Maritime • News • Satellite
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Country Selection Panel */}
        <div className="glass-panel tactical-border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Target className="w-5 h-5 text-[#00FF9D]" />
            SELECT TARGET COUNTRY
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Country Dropdown */}
            <div className="lg:col-span-2">
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-mono mb-2 block">
                Country
              </label>
              <select
                value={selectedCountry?.id || ''}
                onChange={(e) => setSelectedCountry(countries.find(c => c.id === e.target.value))}
                className="w-full bg-black/50 border border-white/10 text-white px-4 py-3 text-sm font-mono"
                data-testid="country-select"
              >
                <option value="">-- Select a country --</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.name} ({country.code}) - {country.region}
                  </option>
                ))}
              </select>
              
              {selectedCountry && (
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-zinc-500" />
                    <span className="text-sm font-bold">{selectedCountry.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-zinc-500" />
                    <span className={`text-sm ${getRegionColor(selectedCountry.region)}`}>
                      {selectedCountry.region}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Time Range */}
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-mono mb-2 block">
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(parseInt(e.target.value))}
                className="w-full bg-black/50 border border-white/10 text-white px-4 py-3 text-sm font-mono"
              >
                <option value={6}>Last 6 Hours</option>
                <option value={12}>Last 12 Hours</option>
                <option value={24}>Last 24 Hours</option>
                <option value={48}>Last 48 Hours</option>
                <option value={72}>Last 72 Hours</option>
                <option value={168}>Last 7 Days</option>
              </select>
            </div>
          </div>

          {/* Data Source Options */}
          <div className="mt-4">
            <label className="text-xs text-zinc-500 uppercase tracking-wider font-mono mb-2 block">
              Include Data Sources
            </label>
            <div className="flex flex-wrap gap-3">
              {[
                { key: 'flights', icon: Plane, label: 'Flight Tracking', color: 'emerald' },
                { key: 'vessels', icon: Ship, label: 'Maritime AIS', color: 'cyan' },
                { key: 'news', icon: Newspaper, label: 'News Intelligence', color: 'orange' },
                { key: 'satellite', icon: Satellite, label: 'Satellite Imagery', color: 'purple' }
              ].map(source => (
                <button
                  key={source.key}
                  onClick={() => setIncludeOptions(prev => ({ ...prev, [source.key]: !prev[source.key] }))}
                  className={`flex items-center gap-2 px-4 py-2 border transition-all ${
                    includeOptions[source.key]
                      ? `bg-${source.color}-500/20 text-${source.color}-400 border-${source.color}-500/50`
                      : 'bg-white/5 text-zinc-500 border-white/10'
                  }`}
                >
                  <source.icon className="w-4 h-4" />
                  <span className="text-xs font-bold">{source.label}</span>
                  {includeOptions[source.key] && <CheckCircle className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={generateAnalysis}
              disabled={!selectedCountry || analyzing}
              className="flex items-center gap-2 px-8 py-3 btn-primary disabled:opacity-50"
              data-testid="generate-analysis-btn"
            >
              {analyzing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>ANALYZING...</span>
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  <span>GENERATE INTELLIGENCE REPORT</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Analysis Loading State */}
        {analyzing && (
          <div className="glass-panel tactical-border p-12 text-center">
            <Loader className="w-16 h-16 text-[#00FF9D] animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Generating Intelligence Report</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Collecting data from multiple OSINT sources...
            </p>
            <div className="flex justify-center gap-6 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><Plane className="w-3 h-3" /> Flight Data</span>
              <span className="flex items-center gap-1"><Ship className="w-3 h-3" /> Maritime AIS</span>
              <span className="flex items-center gap-1"><Newspaper className="w-3 h-3" /> News Feed</span>
              <span className="flex items-center gap-1"><Satellite className="w-3 h-3" /> Satellite</span>
            </div>
          </div>
        )}

        {/* Report Display */}
        {report && !analyzing && (
          <div className="space-y-4">
            {/* Report Header */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed' }}>
                    <Shield className="w-7 h-7 text-[#00FF9D]" />
                    {report.country_name.toUpperCase()} INTELLIGENCE REPORT
                  </h2>
                  <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(report.generated_at).toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {report.region}
                    </span>
                    <span className="px-2 py-0.5 bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30">
                      {report.classification}
                    </span>
                  </div>
                </div>
                <button
                  onClick={exportToPptx}
                  className="flex items-center gap-2 px-4 py-2 bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50 hover:bg-[#00FF9D]/20 transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-xs font-bold">EXPORT PPTX</span>
                </button>
              </div>

              {/* Data Source Summary */}
              <div className="grid grid-cols-4 gap-4">
                <DataSourceCard
                  icon={Plane}
                  label="Flights"
                  value={report.flight_summary?.total_flights || 0}
                  detail={`${report.flight_summary?.potential_military || 0} military`}
                  color="emerald"
                  source={report.flight_summary?.source}
                />
                <DataSourceCard
                  icon={Ship}
                  label="Vessels"
                  value={report.vessel_summary?.total_vessels || 0}
                  detail={`${report.vessel_summary?.naval_vessels || 0} naval`}
                  color="cyan"
                  source={report.vessel_summary?.source}
                />
                <DataSourceCard
                  icon={Newspaper}
                  label="News"
                  value={report.news_summary?.total_articles || 0}
                  detail={`${report.news_summary?.defense_related || 0} defense`}
                  color="orange"
                  source="40+ OSINT Sources"
                />
                <DataSourceCard
                  icon={Satellite}
                  label="Satellite"
                  value={report.satellite_summary?.total_passes || 0}
                  detail={`${report.satellite_summary?.high_resolution_available || 0} high-res`}
                  color="purple"
                  source="Copernicus/NASA"
                />
              </div>
            </div>

            {/* Executive Summary */}
            <CollapsibleSection
              title="EXECUTIVE SUMMARY"
              icon={FileText}
              expanded={expandedSections.executive}
              onToggle={() => toggleSection('executive')}
              color="[#00FF9D]"
            >
              <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                {report.executive_summary}
              </p>
            </CollapsibleSection>

            {/* Key Activities */}
            <CollapsibleSection
              title={`KEY ACTIVITIES (${report.key_activities?.length || 0})`}
              icon={Activity}
              expanded={expandedSections.activities}
              onToggle={() => toggleSection('activities')}
              color="cyan-400"
            >
              <div className="space-y-2">
                {report.key_activities?.map((activity, idx) => (
                  <div key={idx} className="p-3 bg-black/30 border border-white/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-zinc-500 uppercase">{activity.category}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-mono ${
                        activity.significance === 'HIGH' ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                        : activity.significance === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                        : 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30'
                      }`}>
                        {activity.significance}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-300">{activity.activity}</p>
                    <div className="text-[10px] text-zinc-500 mt-1">Source: {activity.source}</div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Flight Data Details */}
            {report.flight_summary?.flights?.length > 0 && (
              <CollapsibleSection
                title={`FLIGHT DATA (${report.flight_summary.total_flights} aircraft)`}
                icon={Plane}
                expanded={expandedSections.flights}
                onToggle={() => toggleSection('flights')}
                color="emerald-400"
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-zinc-500 uppercase border-b border-white/10">
                        <th className="text-left py-2 px-2">Callsign</th>
                        <th className="text-left py-2 px-2">ICAO24</th>
                        <th className="text-left py-2 px-2">Origin</th>
                        <th className="text-right py-2 px-2">Altitude</th>
                        <th className="text-right py-2 px-2">Speed</th>
                        <th className="text-right py-2 px-2">Heading</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.flight_summary.flights.slice(0, 10).map((flight, idx) => (
                        <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-2 px-2 font-mono text-emerald-400">{flight.callsign || 'N/A'}</td>
                          <td className="py-2 px-2 font-mono text-zinc-400">{flight.icao24}</td>
                          <td className="py-2 px-2">{flight.origin_country}</td>
                          <td className="py-2 px-2 text-right">{Math.round(flight.altitude || 0)}m</td>
                          <td className="py-2 px-2 text-right">{Math.round(flight.velocity || 0)}m/s</td>
                          <td className="py-2 px-2 text-right">{Math.round(flight.heading || 0)}°</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            )}

            {/* Vessel Data Details */}
            {report.vessel_summary?.vessels?.length > 0 && (
              <CollapsibleSection
                title={`MARITIME DATA (${report.vessel_summary.total_vessels} vessels)`}
                icon={Ship}
                expanded={expandedSections.vessels}
                onToggle={() => toggleSection('vessels')}
                color="cyan-400"
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-zinc-500 uppercase border-b border-white/10">
                        <th className="text-left py-2 px-2">Name</th>
                        <th className="text-left py-2 px-2">Type</th>
                        <th className="text-left py-2 px-2">Flag</th>
                        <th className="text-right py-2 px-2">Speed</th>
                        <th className="text-left py-2 px-2">Destination</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.vessel_summary.vessels.slice(0, 10).map((vessel, idx) => (
                        <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-2 px-2 font-mono text-cyan-400">{vessel.name}</td>
                          <td className="py-2 px-2">{vessel.type}</td>
                          <td className="py-2 px-2">{vessel.flag}</td>
                          <td className="py-2 px-2 text-right">{vessel.speed} kts</td>
                          <td className="py-2 px-2">{vessel.destination}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            )}

            {/* News Headlines */}
            {report.news_summary?.articles?.length > 0 && (
              <CollapsibleSection
                title={`NEWS INTELLIGENCE (${report.news_summary.total_articles} articles)`}
                icon={Newspaper}
                expanded={expandedSections.news}
                onToggle={() => toggleSection('news')}
                color="orange-400"
              >
                <div className="space-y-2">
                  {report.news_summary.articles.slice(0, 8).map((article, idx) => (
                    <div key={idx} className="p-3 bg-black/30 border border-white/10">
                      <a 
                        href={article.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-white hover:text-[#00FF9D] transition-colors"
                      >
                        {article.title}
                      </a>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                        <span>{article.source}</span>
                        {article.published && <span>{new Date(article.published).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Threat Assessment */}
            <CollapsibleSection
              title="THREAT ASSESSMENT"
              icon={AlertTriangle}
              expanded={expandedSections.threat}
              onToggle={() => toggleSection('threat')}
              color="red-400"
            >
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                {report.threat_assessment}
              </p>
            </CollapsibleSection>

            {/* Recommendations */}
            <CollapsibleSection
              title="RECOMMENDATIONS"
              icon={TrendingUp}
              expanded={expandedSections.recommendations}
              onToggle={() => toggleSection('recommendations')}
              color="[#00FF9D]"
            >
              <ul className="space-y-2">
                {report.recommendations?.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                    <CheckCircle className="w-4 h-4 text-[#00FF9D] mt-0.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>

            {/* Sources & Confidence */}
            <div className="glass-panel tactical-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-zinc-500 uppercase">Sources Used:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {report.sources_used?.map((source, idx) => (
                      <span key={idx} className="px-2 py-1 text-[10px] font-mono bg-white/5 text-zinc-400 border border-white/10">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-zinc-500 uppercase">Confidence Level:</span>
                  <p className="text-sm text-[#00FF9D] mt-1">{report.confidence_level}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Data Source Card Component
function DataSourceCard({ icon: Icon, label, value, detail, color, source }) {
  return (
    <div className={`p-4 bg-black/30 border border-${color}-500/30`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 text-${color}-400`} />
        <span className="text-xs font-bold uppercase">{label}</span>
      </div>
      <div className={`text-2xl font-bold text-${color}-400`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{detail}</div>
      <div className="text-[10px] text-zinc-600 mt-2 font-mono">{source}</div>
    </div>
  );
}

// Collapsible Section Component
function CollapsibleSection({ title, icon: Icon, expanded, onToggle, children, color }) {
  return (
    <div className="glass-panel tactical-border overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 text-${color}`} />
          <span className={`text-sm font-bold uppercase text-${color}`}>{title}</span>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {expanded && (
        <div className="p-4 pt-0 border-t border-white/10">
          {children}
        </div>
      )}
    </div>
  );
}
